import { FormEvent, ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
} from '@fluentui/react-components';
import { CheckmarkFilled, Dismiss24Regular, FluentIcon } from '@fluentui/react-icons';

import { ButtonLoading } from '../loading';

export type TFormDialogProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  title: ReactNode;
  /** Bound react-hook-form `handleSubmit(onSubmit)` - called when the user submits the form. */
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  children: ReactNode;

  // Optional submit button customisation
  submitLabel?: string;
  submitIcon?: ReactNode;
  submitDisabled?: boolean;
  /** Show the spinner on the submit button. Pass the mutation's `isPending`. */
  isSubmitting?: boolean;

  /** Optional className applied to DialogSurface (e.g. for max-width overrides). */
  surfaceClassName?: string;
  /** Optional className applied to DialogContent (e.g. for max-height + scroll). */
  contentClassName?: string;

  /** Override the default Cancel label - most callers shouldn't need this. */
  cancelLabel?: string;
  /** Hide the Cancel button entirely (rare; use only when the close X is the only dismiss path). */
  hideCancel?: boolean;
};

// Wraps the standard form-dialog chrome (Dialog → Surface → <form> → Body → Title → Content → Actions).
// Callers own the form state (useForm, schema, handleSubmit) and pass field components as children;
// FormDialog handles the dialog chrome, submit button, cancel button, and close X uniformly.
const FormDialog = ({
  isOpen,
  setIsOpen,
  title,
  onSubmit,
  children,
  submitLabel = 'Save',
  submitIcon = <CheckmarkFilled />,
  submitDisabled = false,
  isSubmitting = false,
  surfaceClassName,
  contentClassName,
  cancelLabel = 'Cancel',
  hideCancel = false,
}: TFormDialogProps) => (
  <Dialog
    open={isOpen}
    onOpenChange={(_, data) => {
      if (!isSubmitting) setIsOpen(data.open);
    }}
  >
    <DialogSurface className={surfaceClassName}>
      <form onSubmit={onSubmit}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="close" disabled={isSubmitting} icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            {title}
          </DialogTitle>
          <DialogContent className={contentClassName}>{children}</DialogContent>
          <DialogActions>
            <ButtonLoading
              type="submit"
              appearance="primary"
              icon={submitIcon as ReturnType<FluentIcon>}
              loading={isSubmitting}
              disabled={submitDisabled}
            >
              {submitLabel}
            </ButtonLoading>
            {!hideCancel && (
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" disabled={isSubmitting}>
                  {cancelLabel}
                </Button>
              </DialogTrigger>
            )}
          </DialogActions>
        </DialogBody>
      </form>
    </DialogSurface>
  </Dialog>
);

export { FormDialog };
